import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { plansAPI, progressAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import WeeklyChallengeCard from '../../components/WeeklyChallengeCard';
import { motion } from 'framer-motion';
import { CheckCircle2, Play, Clock, ChevronRight, Brain, Apple, Upload, Trophy, Target, Zap, Dumbbell, Eye, Snowflake } from 'lucide-react';

function getDayFocus(drills) {
  if (!drills || drills.length === 0) return '';
  const cats = [...new Set(drills.map(d => d.category).filter(Boolean))];
  return cats.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(' + ');
}

function getDayTime(drills) {
  if (!drills || drills.length === 0) return 0;
  return drills.reduce((sum, d) => sum + (d.duration_minutes || 0), 0);
}

export default function DailyView() {
  const { dayNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dayNum = parseInt(dayNumber);
  const weekNumber = parseInt(searchParams.get('week') || '0');

  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [planRes, progRes] = await Promise.all([
          plansAPI.current(),
          progressAPI.weekly(),
        ]);
        setPlan(planRes.data);
        setProgress(progRes.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [dayNum]);

  const completedSet = new Set(
    progress.filter(p => p.day_number === dayNum).map(p => p.drill_id)
  );

  const day = plan?.days?.find(d => d.day_number === dayNum);
  const drills = day?.drills || [];
  const completedCount = drills.filter(d => completedSet.has(d.id)).length;
  const allDone = drills.length > 0 && completedCount === drills.length;
  const focus = getDayFocus(drills);
  const totalTime = getDayTime(drills);
  const wk = weekNumber || plan?.week_number || 1;

  if (loading) {
    return (
      <Layout showBack>
        <div className="flex justify-center py-24"><div className="spinner" /></div>
      </Layout>
    );
  }

  if (!day) {
    return (
      <Layout showBack>
        <div className="px-4 py-12 text-center text-[#64748B]">Day not found.</div>
      </Layout>
    );
  }

  return (
    <Layout showBack>
      <SubscriptionGate feature="training">
        <div className="px-4 py-6 space-y-6">
          {/* Day Header */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-1">
              {day.is_today && (
                <span className="text-[9px] bg-[#2563EB]/20 text-[#00E5FF] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                  Today
                </span>
              )}
            </div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight" data-testid="daily-view-title">
              {day.day_label || `Day ${dayNum}`}{focus ? ` — ${focus}` : ''}
            </h1>
            <div className="flex items-center gap-4 mt-2">
              <span className="flex items-center gap-1.5 text-xs text-[#64748B]">
                <Clock size={12} />
                {totalTime} min
              </span>
              <span className="text-xs text-[#64748B]">
                Week {plan?.player_week || wk}
              </span>
            </div>
          </motion.div>

          {/* Day Done Celebration Banner */}
          {allDone && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 flex items-center gap-4"
              data-testid="day-done-banner"
            >
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                <Trophy size={24} className="text-[#10B981]" />
              </div>
              <div>
                <p className="font-['Barlow_Condensed'] text-base font-bold text-[#10B981]">
                  {(day.day_label || `Day ${dayNum}`).toUpperCase()} COMPLETE
                </p>
                <p className="text-xs text-[#64748B]">All drills done. Keep the momentum going.</p>
              </div>
            </motion.div>
          )}

          {/* Progress Counter */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="daily-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#64748B] font-semibold">Drills Completed</span>
                <span className="font-['Barlow_Condensed'] text-sm font-bold text-[#0A2242]">
                  {completedCount}/{drills.length}
                </span>
              </div>
              <div className="w-full bg-[#E2E8F0] rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{
                    width: drills.length > 0 ? `${(completedCount / drills.length) * 100}%` : '0%',
                    background: completedCount === drills.length && drills.length > 0
                      ? '#10B981'
                      : 'linear-gradient(to right, #2563EB, #00E5FF)',
                  }}
                />
              </div>
            </div>
          </motion.div>

          {/* Weekly Challenge — pinned at top of daily plan */}
          <WeeklyChallengeCard />

          {/* Drill Sections — grouped by category in fixed order */}
          {(() => {
            const SECTIONS = [
              { key: 'stickhandling', label: 'Stickhandling', icon: Zap,      color: '#2563EB' },
              { key: 'shooting',      label: 'Shooting',      icon: Target,   color: '#FF9100' },
              { key: 'off-ice',       label: 'Off-Ice',       icon: Dumbbell, color: '#10B981' },
              { key: 'video-review',  label: 'Video Review',  icon: Eye,      color: '#00E5FF' },
              { key: 'skating',       label: 'Skating',       icon: Snowflake, color: '#64748B' },
            ];
            const byCat = drills.reduce((acc, d) => {
              const k = (d.category || '').toLowerCase();
              (acc[k] = acc[k] || []).push(d);
              return acc;
            }, {});

            let globalIdx = -1;
            const sections = SECTIONS.map(sec => {
              const items = byCat[sec.key] || [];
              if (items.length === 0) return null;
              const Icon = sec.icon;
              return (
                <div key={sec.key} className="space-y-2">
                  <div className="flex items-center gap-2 pt-1">
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center"
                      style={{ background: `${sec.color}15`, border: `1px solid ${sec.color}40` }}
                    >
                      <Icon size={14} style={{ color: sec.color }} />
                    </div>
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.2em]"
                      style={{ color: sec.color }}
                    >
                      {sec.label}
                    </span>
                    <span className="text-[10px] text-[#94A3B8] font-bold">
                      {items.length} {items.length === 1 ? 'drill' : 'drills'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {items.map((drill) => {
                      globalIdx += 1;
                      const i = globalIdx;
                      const isDone = completedSet.has(drill.id);
                      return (
                        <motion.div
                          key={drill.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 + i * 0.04 }}
                          className={`bg-white border rounded-xl overflow-hidden transition-all ${
                            isDone ? 'border-[#10B981]/30' : 'border-[#E2E8F0] hover:border-[#2563EB]'
                          }`}
                          data-testid={`daily-drill-${i}`}
                        >
                          <div className="flex items-stretch">
                            <div className={`w-20 flex-shrink-0 flex items-center justify-center ${
                              isDone ? 'bg-[#10B981]/10' : 'bg-[#2563EB]/10'
                            }`}>
                              {isDone ? (
                                <CheckCircle2 size={28} className="text-[#10B981]" />
                              ) : (
                                <Play size={24} className="text-[#2563EB] ml-0.5" />
                              )}
                            </div>
                            <div className="flex-1 p-4">
                              <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight mb-1">
                                {drill.title}
                              </h3>
                              <p className="text-xs text-[#64748B] line-clamp-2 mb-2">{drill.description}</p>
                              <div className="flex items-center gap-3">
                                <span className="flex items-center gap-1 text-[10px] text-[#64748B]">
                                  <Clock size={10} /> {drill.duration_minutes}m
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => navigate(`/drill/${drill.id}?week=${wk}&day=${dayNum}`)}
                            className={`w-full py-3 text-center text-xs font-bold uppercase tracking-widest transition-colors border-t ${
                              isDone
                                ? 'border-[#10B981]/20 text-[#10B981]/70 hover:text-[#10B981] hover:bg-[#10B981]/5'
                                : 'border-[#E2E8F0] text-[#2563EB] hover:bg-[#2563EB]/10'
                            }`}
                            data-testid={`start-drill-${i}`}
                          >
                            {isDone ? 'Review Drill' : 'Start Drill'}
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            }).filter(Boolean);

            // Any categories not in the fixed list (edge case): append to the end
            const knownKeys = new Set(SECTIONS.map(s => s.key));
            const leftover = drills.filter(d => !knownKeys.has((d.category || '').toLowerCase()));
            if (leftover.length > 0) {
              sections.push(
                <div key="_other" className="space-y-2">
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">Other</span>
                  </div>
                  {leftover.map((drill) => {
                    globalIdx += 1;
                    const i = globalIdx;
                    const isDone = completedSet.has(drill.id);
                    return (
                      <motion.div
                        key={drill.id}
                        className={`bg-white border rounded-xl overflow-hidden ${isDone ? 'border-[#10B981]/30' : 'border-[#E2E8F0]'}`}
                        data-testid={`daily-drill-${i}`}
                      >
                        <div className="flex items-stretch">
                          <div className={`w-20 flex-shrink-0 flex items-center justify-center ${isDone ? 'bg-[#10B981]/10' : 'bg-[#2563EB]/10'}`}>
                            {isDone ? <CheckCircle2 size={28} className="text-[#10B981]" /> : <Play size={24} className="text-[#2563EB] ml-0.5" />}
                          </div>
                          <div className="flex-1 p-4">
                            <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight mb-1">{drill.title}</h3>
                            <p className="text-xs text-[#64748B] line-clamp-2 mb-2">{drill.description}</p>
                            <span className="flex items-center gap-1 text-[10px] text-[#64748B]"><Clock size={10} /> {drill.duration_minutes}m</span>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/drill/${drill.id}?week=${wk}&day=${dayNum}`)}
                          className={`w-full py-3 text-center text-xs font-bold uppercase tracking-widest border-t ${isDone ? 'border-[#10B981]/20 text-[#10B981]/70' : 'border-[#E2E8F0] text-[#2563EB]'}`}
                          data-testid={`start-drill-${i}`}
                        >{isDone ? 'Review Drill' : 'Start Drill'}</button>
                      </motion.div>
                    );
                  })}
                </div>
              );
            }

            return <div className="space-y-5">{sections}</div>;
          })()}

          {/* Mental Tip — section 4 */}
          {day.mental_tip && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-2"
            >
              <div className="flex items-center gap-2 pt-1">
                <div className="w-7 h-7 rounded-md flex items-center justify-center bg-[#A855F7]/15 border border-[#A855F7]/40">
                  <Brain size={14} className="text-[#A855F7]" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A855F7]">Mental Tip</span>
              </div>
              <div className="bg-white border border-[#A855F7]/20 rounded-xl p-4" data-testid="daily-mental-tip">
                <p className="text-sm text-[#0A2242] leading-relaxed">{day.mental_tip}</p>
              </div>
            </motion.div>
          )}

          {/* Nutrition Tip — section 5 */}
          {day.nutrition_tip && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="space-y-2"
            >
              <div className="flex items-center gap-2 pt-1">
                <div className="w-7 h-7 rounded-md flex items-center justify-center bg-[#10B981]/15 border border-[#10B981]/40">
                  <Apple size={14} className="text-[#10B981]" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#10B981]">Nutrition Tip</span>
              </div>
              <div className="bg-white border border-[#10B981]/20 rounded-xl p-4" data-testid="daily-nutrition-tip">
                <p className="text-sm text-[#0A2242] leading-relaxed">{day.nutrition_tip}</p>
              </div>
            </motion.div>
          )}

          {/* Upload CTA */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={() => navigate('/feedback')}
            className="w-full bg-transparent border-2 border-[#E2E8F0] hover:border-[#2563EB] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2"
            data-testid="daily-upload-cta"
          >
            <Upload size={16} />
            Upload Your Rep for Coach Feedback
          </motion.button>
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
